import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OrdersList from "./OrdersList";
import Button from "../../components/shared/buttons/button";
import { FilterSvg } from "../../svgFiles/FilterSvg";
import { ChevronDownSvg } from "../../svgFiles/ChevronDownSvg";
import { UploadSvg } from "../../svgFiles/UploadSvg";
import { AddSvg } from "../../svgFiles/AddSvg";
import { SearchSvg } from "../../svgFiles/SearchSvg";
import UploadeTrackingModal from "../../components/shared/modalContent/UploadeTrackingModal";
import FilterManager from "./FilterManager";
import { tabConfig } from "../enums/orderEnums";
import CaptifyCards from "./CaptifyCards";
import { Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  setSearch,
  setOrdersTextSearch,
  setActiveTab,
  setFilterPanelOpen,
  hasAdvancedOrderFilters,
} from "../../app/orderApi/filterSlice";
import ConfirmationModal from "../../components/shared/modalContent/ConfirmationModal";
import RequestDemoUnitModal from "../../components/va/RequestDemoUnitModal";
import { useSyncAllOrdersMutation } from "../../app/orderApi/OrderApi";
import { SyncSvg } from "../../svgFiles/syncSvg.jsx";
import Loader from "../../components/shared/loader/Loader";
import { showToast, useDebounce, getClassByType } from "../../helpers/Utils";
import { useAuth } from "../auth/AuthContext";
import { useOrderSocket } from "../../hooks/useOrderSocket";
import { P } from "../../utils/permissions";

function TabStatusLabel({ tabKey, label }) {
  if (tabKey === "all") {
    return <span className="orders-tab-plain-label cursor-pointer">{label}</span>;
  }

  const statusKey = tabKey === "999" ? 999 : Number(tabKey);
  const status = getClassByType(statusKey);
  const code = tabKey === "999" ? "999" : tabKey.padStart(2, "0");

  return (
    <span className={`${status.class} status-badge cursor-pointer`}>
      <span className="borderBar pe-2 me-1">{code}</span>
      {status.title}
    </span>
  );
}

const OrdersTabs = ({
  intialSearch = "",
  setIntialSearch,
  onOpenCreateUpload,
  onOpenCreateManual,
  onOpenCreateDevice,
  showStatusCards = true,
  variant = "admin",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Socket only while the orders list is mounted — avoids connect/refresh on other routes.
  useOrderSocket({ enabled: true });
  const { search, activeTab, filterPanelOpen } = useSelector(
    (state) => state.filters
  );
  const currentTab = activeTab || "all";
  const [showFilter, setShowFilter] = useState(
    () => filterPanelOpen || hasAdvancedOrderFilters(search)
  );
  const debouncedSearch = useDebounce(intialSearch, 500);

  const updateFilterPanel = (open) => {
    setShowFilter(open);
    dispatch(setFilterPanelOpen(open));
  };

  useEffect(() => {
    if (filterPanelOpen || hasAdvancedOrderFilters(search)) {
      setShowFilter(true);
    }
  }, [filterPanelOpen, search]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDemoUnitModal, setShowDemoUnitModal] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false);
  const tabDropdownPendingClose = useRef(false);

  const resetPagination = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has("page")) return;
    params.delete("page");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
  };

  const handleTabFilterClick = (key) => {
    tabDropdownPendingClose.current = true;
    if (key !== currentTab) {
      resetPagination();
    }
    dispatch(setActiveTab(key));
  };

  useEffect(() => {
    if (!tabDropdownPendingClose.current || ordersLoading) {
      return;
    }
    setTabDropdownOpen(false);
    tabDropdownPendingClose.current = false;
  }, [ordersLoading]);

  useEffect(() => {
    const currentText =
      search && typeof search === "object" ? search.search || "" : "";

    // Avoid wiping saved text before debounce catches up after navigation back.
    if (!debouncedSearch && currentText && intialSearch === currentText) {
      return;
    }
    if (debouncedSearch === currentText) {
      return;
    }

    dispatch(setOrdersTextSearch(debouncedSearch));
  }, [debouncedSearch, dispatch, search, intialSearch]);

  useEffect(() => {
    if (search?.search && activeTab !== "all") {
      dispatch(setActiveTab("all"));
    }
  }, [search?.search, activeTab, dispatch]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [syncAllOrders, { isLoading: isSyncing }] = useSyncAllOrdersMutation();
  const [removeSelection, setRemoveSelection] = useState(true);
  const { hasPermission } = useAuth();
  const canSync = hasPermission(P.SHOPIFY_SYNC);
  const canUploadTracking = hasPermission(P.ORDERS_UPLOAD_TRACKING);
  const canUploadInvoice = hasPermission(P.VA_INVOICE_UPLOAD);
  const canCreateVaDeviceOrder = hasPermission(P.VA_DEVICE_ORDER_CREATE);
  const canRequestDemoUnit = hasPermission(P.VA_DEMO_ORDER_CREATE);
  const canCreateOrder = hasPermission(P.AUDIOLOGIST_ORDER_CREATE);
  const isAudiologistVariant = variant === "audiologist";
  const isDistributorVariant = variant === "distributor";
  const showUploadInvoiceOption =
    !isAudiologistVariant && !isDistributorVariant && canUploadInvoice;
  const showManualCreateOption = isAudiologistVariant && canCreateOrder;
  const showVaDeviceCreateOption =
    !isAudiologistVariant &&
    !isDistributorVariant &&
    canCreateVaDeviceOrder;
  const showRequestDemoUnit =
    isDistributorVariant && canRequestDemoUnit;
  const showCreateOrder =
    showUploadInvoiceOption || showManualCreateOption;
  const showMoreActions = canSync || canUploadTracking || showVaDeviceCreateOption;

  const activeTabLabel =
    tabConfig.find((tab) => tab.key === currentTab)?.label ?? "All";

  const renderActiveTabToggle = () => {
    if (currentTab === "all") {
      return <span className="flex-grow-1 orders-tab-plain-label">All</span>;
    }
    return (
      <span className="flex-grow-1 d-flex">
        <TabStatusLabel tabKey={currentTab} label={activeTabLabel} />
      </span>
    );
  };

  const handleSyncOrders = async () => {
    try {
      showToast("Order sync has started in the background!", "success");
      await syncAllOrders().unwrap();
    } catch (error) {
      console.error("Failed to sync orders:", error);
    } finally {
      setShowConfirmation(false);
    }
  };

  return (
    <div className="orders-page">
      {isSyncing && <Loader />}
      {showStatusCards && <CaptifyCards orders={orders} />}
      <UploadeTrackingModal
        show={showUploadModal}
        setShow={setShowUploadModal}
      />
      {showRequestDemoUnit && (
        <RequestDemoUnitModal
          show={showDemoUnitModal}
          onClose={() => setShowDemoUnitModal(false)}
        />
      )}
      <ConfirmationModal
        show={showConfirmation}
        setShow={setShowConfirmation}
        message="Are you sure you want to sync all orders?"
        onConfirm={handleSyncOrders}
        onCancel={() => setShowConfirmation(false)}
        isLoading={isSyncing}
        icon={SyncSvg}
        iconProps={{ width: 48, height: 48 }}
      />

      <div
        className={`orderWrapper ${
          variant === "distributor"
            ? "va-distributor-orders"
            : variant === "audiologist"
              ? "va-audiologist-orders"
              : ""
        }`.trim()}
      >
        <div className="tabs-head orders-toolbar">
          {showFilter ? (
            <FilterManager
              onClearAllFilters={() => dispatch(setActiveTab("all"))}
              setRemoveSelection={setRemoveSelection}
              ordersLoading={ordersLoading}
            />
          ) : (
            <>
              <div className="orders-toolbar-left">
                <Dropdown
                  className="custoom-dropdown orders-tab-dropdown"
                  show={tabDropdownOpen}
                  onToggle={setTabDropdownOpen}
                >
                  <Dropdown.Toggle
                    id="orders-tab-dropdown"
                    variant="unset"
                    className="d-flex justify-content-between align-items-center btn-grey orders-tab-toggle"
                  >
                    {renderActiveTabToggle()}
                    <ChevronDownSvg />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="mt-3 orders-tab-menu">
                    {tabConfig.map(({ key, label }) => (
                      <div
                        key={key}
                        className={`orders-tab-menu-item cursor-pointer ${
                          currentTab === key ? "active" : ""
                        }`}
                        onClick={() => handleTabFilterClick(key)}
                        role="menuitem"
                      >
                        <TabStatusLabel tabKey={key} label={label} />
                      </div>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <div
                  className={`search orders-toolbar-search d-flex align-items-center gap-2 flex-grow-1 ${
                    isSearchFocused ? "activeSearch" : ""
                  }`}
                >
                  <SearchSvg
                    className={isSearchFocused ? "focusedSvg" : ""}
                  />
                  <input
                    type="search"
                    className="w-100"
                    placeholder="Search by order ID, name, email or PO number"
                    value={intialSearch}
                    onChange={(e) => setIntialSearch?.(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                  />
                </div>
              </div>

              <div className="orders-toolbar-actions d-flex gap-2 align-items-center">
                <Button
                  label="Filters"
                  icon={<FilterSvg />}
                  className="btn-grey"
                  onClick={() => {
                    dispatch(setActiveTab("all"));
                    updateFilterPanel(true);
                  }}
                />
                {showMoreActions && (
                  <Dropdown className="custoom-dropdown">
                    <Dropdown.Toggle
                      id="custom-dropdown"
                      variant="unset"
                      className="d-flex justify-content-between align-items-center btn-grey orders-more-actions"
                    >
                      <span className="flex-grow-1">More Actions</span>
                      <ChevronDownSvg />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="mt-3">
                      {/* {canSync && (
                        <li
                          className="text-nowrap d-flex gap-2 align-items-center px-2 cursor-pointer"
                          onClick={() => setShowConfirmation(true)}
                        >
                          <SyncSvg /> Sync all orders from shopify
                        </li>
                      )} */}
                      {canUploadTracking && (
                        <li
                          className="text-nowrap d-flex gap-2 align-items-center px-2 cursor-pointer"
                          onClick={() => setShowUploadModal(true)}
                        >
                          <UploadSvg /> Upload tracking number sheet
                        </li>
                      )}
                      {showVaDeviceCreateOption && (
                        <li
                          className="text-nowrap d-flex gap-2 align-items-center px-2 cursor-pointer"
                          onClick={onOpenCreateDevice}
                        >
                          <UploadSvg /> Register glasses to VA
                        </li>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
                {showRequestDemoUnit && (
                  <Button
                    label="Request demo"
                    icon={<AddSvg />}
                    className="cmn_btn btn-medium d-flex gap-2 align-items-center orders-create-btn"
                    onClick={() => setShowDemoUnitModal(true)}
                  />
                )}
                {showCreateOrder &&
                  (showUploadInvoiceOption ? (
                  <Dropdown className="custoom-dropdown orders-create-dropdown">
                    <Dropdown.Toggle
                      id="create-order-dropdown"
                      variant="unset"
                      className="cmn_btn btn-medium d-flex gap-2 align-items-center orders-create-btn"
                    >
                      <AddSvg /> <span>Create Order</span>
                      <ChevronDownSvg />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="mt-3">
                      {showUploadInvoiceOption && (
                        <li
                          className="text-nowrap d-flex gap-2 align-items-center px-2 cursor-pointer"
                          onClick={onOpenCreateUpload}
                        >
                          <UploadSvg /> Upload PO from VA
                        </li>
                      )}
                      {showManualCreateOption && (
                        <li
                          className="text-nowrap d-flex gap-2 align-items-center px-2 cursor-pointer"
                          onClick={onOpenCreateManual}
                        >
                          <AddSvg /> Create Manually
                        </li>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                  ) : (
                    <Button
                      label="Create Order"
                      icon={<AddSvg />}
                      className="cmn_btn btn-medium d-flex gap-2 align-items-center orders-create-btn"
                      onClick={onOpenCreateManual}
                    />
                  ))}
              </div>
            </>
          )}

          {showFilter && (
            <Button
              label="Cancel"
              className="btn-grey ms-auto"
              onClick={() => {
                updateFilterPanel(false);
                dispatch(setActiveTab("all"));
                dispatch(setSearch({}));
              }}
            />
          )}
        </div>

        <div className="tab-content mt-0">
          <div className="tab-pane active">
            <OrdersList
              removeSelection={removeSelection}
              setShowFilter={setShowFilter}
              setOrders={setOrders}
              onLoadingChange={setOrdersLoading}
              type={currentTab}
              activeTab={currentTab}
              variant={variant}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersTabs;
